import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { TopicGrid } from "../components/TopicGrid";
import { SpreadPicker } from "../components/SpreadPicker";
import { DataForm } from "../components/DataForm";
import { CardReveal } from "../components/CardReveal";
import { ReadingView } from "../components/ReadingView";
import { TOPICS, SPREADS } from "../data/config";
import { drawCards } from "../data/cards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  birthdate: "",
  birthtime: "",
  birthplace: "",
  situation: "",
  question: "",
};

export default function Home() {
  const [step, setStep] = useState("topic");
  const [topic, setTopic] = useState(null);
  const [deck, setDeck] = useState("lenormand");
  const [spread, setSpread] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [drawn, setDrawn] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const topicObj = TOPICS.find((t) => t.id === topic);
  const spreadObj = SPREADS.find((s) => s.id === spread);

  const handleTopic = (id) => {
    setTopic(id);
    setStep("spread");
  };

  const handleDraw = () => {
    const cards = drawCards(deck, spreadObj.positions.length);
    setDrawn(cards);
    setFlipped(false);
    setStep("draw");
  };

  const handleReveal = async () => {
    setFlipped(true);
    setLoading(true);
    const payload = {
      topic,
      topicLabel: topicObj.label,
      spread,
      spreadLabel: spreadObj.label,
      deck,
      ...form,
      cards: drawn.map((c, i) => ({
        position: spreadObj.positions[i],
        name: c.name,
        keyword: c.keyword,
        image: c.img,
      })),
    };
    try {
      await new Promise((r) => setTimeout(r, 900));
      const res = await axios.post(`${API}/reading`, payload);
      setResult(res.data);
      setStep("result");
    } catch (e) {
      setFlipped(false);
      setStep("form");
      toast.error("Die Analyse konnte nicht erstellt werden. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep("topic");
    setTopic(null);
    setSpread(null);
    setDeck("lenormand");
    setForm(emptyForm);
    setDrawn([]);
    setFlipped(false);
    setResult(null);
  };

  return (
    <Layout>
      {step === "topic" && <TopicGrid selected={topic} onSelect={handleTopic} />}

      {step === "spread" && (
        <SpreadPicker
          deck={deck}
          onDeck={setDeck}
          spread={spread}
          onSpread={setSpread}
          onBack={() => setStep("topic")}
          onNext={() => setStep("form")}
        />
      )}

      {step === "form" && (
        <DataForm
          data={form}
          onChange={setForm}
          onBack={() => setStep("spread")}
          onSubmit={handleDraw}
        />
      )}

      {step === "draw" && (
        <CardReveal
          cards={drawn}
          positions={spreadObj.positions}
          flipped={flipped}
          onReveal={handleReveal}
          loading={loading}
        />
      )}

      {step === "result" && result && (
        <ReadingView
          result={result}
          cards={drawn}
          positions={spreadObj.positions}
          meta={{ topicLabel: topicObj.label, spreadLabel: spreadObj.label }}
          onRestart={restart}
        />
      )}
    </Layout>
  );
}
